import { getInfraStatus } from '@/utils/infraStatus';

export interface ClusterIconOptions {
	count: number;
	normalCount: number;
	criticalCount: number;
	noDataCount: number;
	selectedCount: number;
	size?: number;
}

const NORMAL_COLOR = '#04628C';
const CRITICAL_COLOR = '#F46A6A';
const NO_DATA_COLOR = '#A6B0BD';
const TRACK_COLOR = '#E6EAEF';
const SELECTED_COLOR = '#22C55E';

const RING_STROKE_WIDTH = 14;
const SELECTED_STROKE_WIDTH = 10;
const SEGMENT_GAP = 0.012;

interface RingSegment {
	value: number;
	color: string;
}

function polarToCartesian(cx: number, cy: number, r: number, fraction: number): [number, number] {
	const angle = fraction * 2 * Math.PI - Math.PI / 2;
	return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function describeArc(cx: number, cy: number, r: number, startFraction: number, endFraction: number): string {
	const [startX, startY] = polarToCartesian(cx, cy, r, startFraction);
	const [endX, endY] = polarToCartesian(cx, cy, r, endFraction);
	const largeArcFlag = endFraction - startFraction > 0.5 ? 1 : 0;
	return `M ${startX.toFixed(3)} ${startY.toFixed(3)} A ${r} ${r} 0 ${largeArcFlag} 1 ${endX.toFixed(3)} ${endY.toFixed(3)}`;
}

const iconCache = new Map<string, string>();

function generateClusterSvg(options: ClusterIconOptions): string {
	const { count, normalCount, criticalCount, noDataCount, selectedCount, size = 200 } = options;
	const center = size / 2;
	const ringRadius = center * 0.86 - RING_STROKE_WIDTH / 2;
	const coreRadius = center * 0.6;
	const selectedRadius = ringRadius - RING_STROKE_WIDTH / 2 - SELECTED_STROKE_WIDTH / 2 - 3;

	const total = Math.max(1, normalCount + criticalCount + noDataCount);
	const segments: RingSegment[] = [
		{ value: normalCount, color: NORMAL_COLOR },
		{ value: criticalCount, color: CRITICAL_COLOR },
		{ value: noDataCount, color: NO_DATA_COLOR },
	].filter((segment) => segment.value > 0);

	const track = `<circle cx="${center}" cy="${center}" r="${ringRadius}" fill="none" stroke="${TRACK_COLOR}" stroke-width="${RING_STROKE_WIDTH}" />`;

	const singleSegment = segments.length === 1;
	let runningFraction = 0;
	const arcs = segments
		.map((segment) => {
			const fraction = segment.value / total;
			const start = runningFraction;
			const end = runningFraction + fraction;
			runningFraction = end;

			const gap = singleSegment ? 0 : Math.min(SEGMENT_GAP, fraction * 0.25);
			const drawStart = start + gap;
			const drawEnd = end - gap;
			if (drawEnd <= drawStart) return '';

			if (singleSegment) {
				return `<circle cx="${center}" cy="${center}" r="${ringRadius}" fill="none" stroke="${segment.color}" stroke-width="${RING_STROKE_WIDTH}" />`;
			}

			return `<path d="${describeArc(center, center, ringRadius, drawStart, drawEnd)}" fill="none" stroke="${segment.color}" stroke-width="${RING_STROKE_WIDTH}" stroke-linecap="round" />`;
		})
		.join('\n\t');

	const selectedRing =
		selectedCount > 0
			? `<circle cx="${center}" cy="${center}" r="${selectedRadius}" fill="none" stroke="${SELECTED_COLOR}" stroke-width="${SELECTED_STROKE_WIDTH}" stroke-dasharray="${(2 * Math.PI * selectedRadius * 0.04).toFixed(2)} ${(2 * Math.PI * selectedRadius * 0.04).toFixed(2)}" />`
			: '';

	let coreColor: string;
	let textColor = '#FFFFFF';
	if (normalCount > 0) {
		coreColor = NORMAL_COLOR;
	} else if (criticalCount > 0) {
		coreColor = CRITICAL_COLOR;
	} else {
		coreColor = NO_DATA_COLOR;
		textColor = '#000000';
	}

	const core = `<circle cx="${center}" cy="${center}" r="${coreRadius}" fill="${coreColor}" />`;

	const digits = String(count).length;
	const fontSizeForThreeOrMore = digits === 3 ? center * 0.5 : center * 0.4;
	const fontSize = digits <= 2 ? center * 0.6 : fontSizeForThreeOrMore;

	const label = `<text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="bold" fill="${textColor}">${count}</text>`;

	return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
	${track}
	${arcs}
	${selectedRing}
	${core}
	${label}
</svg>`;
}

export function generateClusterIconUrl(options: ClusterIconOptions): string {
	const cacheKey = `${options.count}_${options.normalCount}_${options.criticalCount}_${options.noDataCount}_${options.selectedCount}`;

	const cached = iconCache.get(cacheKey);
	if (cached) return cached;

	const svg = generateClusterSvg(options);
	const url = `data:image/svg+xml;base64,${btoa(svg)}`;

	iconCache.set(cacheKey, url);
	return url;
}

export function isInfraAlerted(infra: any): boolean {
	return getInfraStatus(infra) === 'critical';
}

export function clearClusterIconCache(): void {
	iconCache.clear();
}
